import React, { useState, useEffect } from 'react'
import './Signup.css'
import { Link, useNavigate } from 'react-router-dom'
import { Formik } from 'formik'
import { signInWithEmailAndPassword, GoogleAuthProvider, signInWithPopup } from 'firebase/auth'
import { auth } from '../../Firebaseconfig'
import Navbar from '../MainComponents/Navbar'
import { useTranslation } from 'react-i18next'

function Signin() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [showPassword, setShowPassword] = useState(false)
  const [googleError, setGoogleError] = useState('')
  const [isLoaded, setIsLoaded] = useState(false)

  useEffect(() => {
    setIsLoaded(true)
  }, [])

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed')
            obs.unobserve(entry.target)
          }
        })
      },
      { root: null, rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    )
    const els = document.querySelectorAll(
      '.reveal, .reveal-left, .reveal-right, .reveal-scale, .reveal-stagger'
    )
    els.forEach((el) => observer.observe(el))
    return () => els.forEach((el) => observer.unobserve(el))
  }, [])

  const handleGoogleSignIn = async () => {
    const provider = new GoogleAuthProvider()
    try {
      await signInWithPopup(auth, provider)
      navigate('/dashboard')
    } catch (error) {
      setGoogleError(error.message)
    }
  }

  return (
    <div className="container">
      <Navbar />
    <div className="signup-page">
      <div className={`signup-card ${isLoaded ? 'animate-in' : ''}`}>

        <span className="signup-label">{t('auth.welcomeBack')}</span>
        <h1 className="signup-title">
          {t('auth.logInAccount')}<span className="signup-dot">.</span>
        </h1>
        <p className="signup-subtext">
          {t('auth.dontHaveAccount')}{' '}
          <Link to="/signup" className="signup-login-link">{t('auth.signUp')}</Link>
        </p>

        <Formik
          initialValues={{ email: '', password: '' }}
          validate={(values) => {
            const errors = {}
            if (!values.email) {
              errors.email = t('auth.required')
            } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)) {
              errors.email = t('auth.invalidEmail')
            }
            if (!values.password) {
              errors.password = t('auth.required')
            }
            return errors
          }}
          onSubmit={async (values, { setSubmitting, setFieldError }) => {
            try {
              await signInWithEmailAndPassword(auth, values.email, values.password)
              navigate('/dashboard')
            } catch (error) {
              if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
                setFieldError('email', t('auth.invalidEmailPass'))
              } else if (error.code === 'auth/invalid-email') {
                setFieldError('email', t('auth.invalidEmail'))
              } else {
                setFieldError('email', error.message)
              }
            } finally {
              setSubmitting(false)
            }
          }}
        >
          {({
            values, errors, touched,
            handleChange, handleBlur, handleSubmit, isSubmitting,
          }) => (
            <form className="signup-form" onSubmit={handleSubmit}>

              <div className="signup-field">
                <label className="signup-field-label">{t('auth.email')}</label>
                <div className="signup-input-wrapper">
                  <input
                    className={`signup-input ${errors.email && touched.email ? 'signup-input--error' : ''}`}
                    type="email"
                    name="email"
                    placeholder="michal.masiak@anywhere.co"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.email}
                  />
                  <span className="signup-input-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                      <polyline points="22,6 12,13 2,6"/>
                    </svg>
                  </span>
                </div>
                {errors.email && touched.email && (
                  <span className="signup-error">{errors.email}</span>
                )}
              </div>

              <div className="signup-field">
                <label className="signup-field-label signup-field-label--accent">{t('auth.password')}</label>
                <div className="signup-input-wrapper signup-input-wrapper--focus">
                  <input
                    className={`signup-input ${errors.password && touched.password ? 'signup-input--error' : ''}`}
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    placeholder="········"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.password}
                  />
                  <button
                    type="button"
                    className="signup-eye-btn"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label="Toggle password visibility"
                  >
                    {showPassword ? (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                        <line x1="1" y1="1" x2="23" y2="23"/>
                      </svg>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                    )}
                  </button>
                </div>
                {errors.password && touched.password && (
                  <span className="signup-error">{errors.password}</span>
                )}
              </div>

              {googleError && <span className="signup-error">{googleError}</span>}

              <div className="signup-actions">
                <button
                  type="button"
                  className="signup-btn-secondary"
                  onClick={handleGoogleSignIn}
                >
                  {t('auth.googleSignIn')}
                </button>
                <button
                  type="submit"
                  className="signup-btn-primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? t('auth.loggingIn') : t('auth.logIn')}
                </button>
              </div>

            </form>
          )}
        </Formik>

      </div>
    </div>
    </div>
  )
}

export default Signin